<template>
  <q-btn
    :class="[
      ai ? 'ai' : '',
      fluid ? 'fluid' : '',
      link ? 'link' : '',
      text ? 'text' : '',
    ]"
    :color="color"
    no-caps
    unelevated
  >
    <template v-for="(slot, index) of Object.keys($slots)" :key="index" #[slot]>
      <slot :name="slot"></slot>
    </template>
  </q-btn>
</template>

<script setup>
import { colors } from "quasar";

const { getPaletteColor, lighten } = colors;

const props = defineProps({
  ai: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: "primary",
  },
  fluid: {
    type: Boolean,
    default: true,
  },
  link: {
    type: Boolean,
  },
  text: {
    type: Boolean,
  },
});

const darkenColor = computed(() => {
  return lighten(getPaletteColor(props.color), -20);
});

const lightenColor = computed(() => {
  return lighten(getPaletteColor(props.color), 40);
});

const disabledColor = computed(() => {
  return lighten(getPaletteColor(props.color), 67);
});
</script>

<style lang="scss" scoped>
.q-btn {
  transition:
    background-color,
    border-color,
    color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

  &:not(.fluid) {
    min-width: 150px;
  }

  &.disabled {
    opacity: 1 !important;
  }

  &--dense {
    &.q-btn--round {
      min-width: 24px;
      min-height: 24px;
    }
  }

  &--fab {
    padding: 0;
    min-height: 48px;
    min-width: 48px;
  }

  &--flat {
    &.disabled {
      color: v-bind(disabledColor) !important;
    }

    &.link,
    &.text {
      :deep(.q-focus-helper) {
        display: none;
      }
    }
  }

  &--flat:not([disabled]) {
    &.link,
    &.text {
      &:hover {
        :deep(.q-btn__content) {
          :not(i, svg) {
            text-decoration: underline;
          }

          &:not(:has(i, svg)) {
            text-decoration: underline;
          }
        }
      }
    }

    &.link {
      :deep(.q-btn__content) {
        :not(i, svg) {
          text-decoration: underline;
        }

        &:not(:has(i, svg)) {
          text-decoration: underline;
        }
      }
    }
  }

  &--outline {
    background-color: $neutral-10 !important;

    &:before {
      border-width: 2px;
    }

    &.disabled {
      &:before {
        border: 2px solid v-bind(disabledColor) !important;
      }
      color: v-bind(disabledColor) !important;
    }

    &:not([disabled]) {
      &.text-primary {
        :deep(.q-focus-helper) {
          display: none;
        }

        &:hover {
          background-color: $surface !important;
        }
      }

      &:hover {
        color: v-bind(darkenColor) !important;

        &:before {
          border: 2px solid v-bind(darkenColor) !important;
        }
      }
    }
  }

  &--round {
    min-width: 28px;
    min-height: 28px;
  }

  &.ai {
    color: $ai-20 !important;

    &:before {
      border: 2px solid transparent;

      background:
        linear-gradient(white, white) padding-box,
        $ai-10 border-box !important;
    }
  }

  :not(&--flat, &--outline, [disabled]) {
    :deep(.q-focus-helper) {
      display: none;
    }

    &:hover {
      background-color: v-bind(darkenColor) !important;
    }
  }

  :not(&--flat, &--outline) {
    &[disabled] {
      background-color: v-bind(disabledColor) !important;
    }
  }

  &:focus {
    outline: 2px solid v-bind(lightenColor) !important;
  }
}
</style>
